// ============================================================
// replace-text.js
// ------------------------------------------------------------
// แต่ละโครงการเรียกชื่อค่าใช้จ่ายไม่เหมือนกัน (เช่น "ค่าส่วนกลาง" บางที่เรียก "ค่าบำรุงหมู่บ้าน")
// ไฟล์นี้ทำหน้าที่แทนที่คำเริ่มต้นในระบบ ด้วยคำที่โครงการตั้งไว้เอง
//
// ใช้ 2 ที่:
//   1. replaceText(docDefinition)  → แทนคำในเอกสาร pdfmake ก่อนสั่งพิมพ์
//   2. replaceTextHTML()           → แทนคำในหน้าจอ (เดิน DOM หลัง render)
//      ต้องเรียก loadReplaceTextHTML() ก่อนหนึ่งครั้ง เพื่อโหลดคำจาก API มา cache ไว้ 30 นาที
// ============================================================

import { GET } from "../core/api.js";
import { tokens } from "../core/storage.js";

// คำเริ่มต้นของระบบ + รูปแบบการสะกดอื่น ๆ ที่เจอในเอกสารเก่า
// key = ชื่อ field ใน setting ของโครงการ, pattern = คำทุกแบบที่ต้องถูกแทนที่, fallback = คำเริ่มต้น
const TEXT_RULES = [
  { key: "water_bill_text", fallback: "ค่าน้ำประปา", pattern: /ค่าน้ำประปา|ค่าน้ำ ประปา|ค่าน้ำประปา|ค่าใช้น้ำ/g },
  { key: "meter_connect_cost_text", fallback: "ค่าต่อมิเตอร์", pattern: /ค่าต่อกลับ มิเตอร์|ค่าต่อ มิเตอร์|ค่าต่อมิเตอร์|ค่าต่อกลับมิเตอร์/g },
  { key: "waste_cost_text", fallback: "ค่าขยะ", pattern: /ค่าขยะ|ค่าจัดเก็บขยะ|ค่าบริการจัดเก็บขยะ/g },
  { key: "common_expenses_text", fallback: "ค่าส่วนกลาง", pattern: /ค่าใช้จ่ายส่วนกลาง|ค่าส่วนกลาง|ค่าส่วน\s*กลาง|ค่า\s*ส่วนกลาง|ค่า ส่วนกลาง|ค่าใช้จ่าย\s*ส่วนกลาง/g },
  { key: "fire_cost_text", fallback: "ค่าเบี้ยประกัน", pattern: /ค่าประกันอัคคีภัย|ค่าเบี้ยประกัน/g },
  { key: "cable_cost_text", fallback: "ค่าเคเบิล", pattern: /ค่าเคเบิล|ค่าบริการเคเบิล/g },
  // ตัวนี้ต้องอยู่ท้ายสุดเสมอ เพราะ pattern ครอบคำว่า "ค่าบริการ" กว้างที่สุด ถ้าขึ้นก่อนจะไปกินคำอื่น
  { key: "equipment_maintenance_fee_text", fallback: "ค่าบริการอุปกรณ์", pattern: /ค่ารักษา อุปกรณ์น้ำ|ค่าบริการรักษาอุปกรณ์น้ำ|ค่ารักษาอุปกรณ์การใช้น้ำ|ค่าบริการอุปกรณ์|ค่ารักษาอุปกรณ์น้ำ|ค่ารักษาอุปกรณ์|ค่าบริการ\s*อุปกรณ์|ค่ารักษา อุปกรณ์|ค่ารักษา\s*อุปกรณ์|ค่าบริการ/g },
];

// อายุ cache ของคำที่โหลดมาเก็บใน localStorage = 30 นาที
const CACHE_MS = 30 * 60 * 1000;

// ดึงคำที่โครงการตั้งไว้จาก API (ถ้าไม่ได้ตั้งไว้ใช้คำเริ่มต้น)
const fetchTexts = async () => {
  const result = await GET(tokens, "v1/TH/water-manage/detail/" + localStorage.getItem("water_id"), null);
  const data = result?.data?.data || {};
  const texts = {};
  TEXT_RULES.forEach((rule) => {
    texts[rule.key] = data[rule.key] || rule.fallback;
  });
  return texts;
};

// แทนคำในเอกสาร pdfmake — แปลง docDefinition เป็น string แล้ว replace ทีเดียวทั้งก้อน
export const replaceText = async (docDefinition) => {
  const texts = await fetchTexts();
  let text = JSON.stringify(docDefinition);
  TEXT_RULES.forEach((rule) => {
    text = text.replace(rule.pattern, texts[rule.key]);
  });
  return JSON.parse(text);
};

// โหลดคำมาเก็บใน localStorage (ข้ามถ้ายังไม่หมดอายุ)
// เรียกตอนเข้าหน้าจอ ก่อนที่จะเรียก replaceTextHTML()
export const loadReplaceTextHTML = async () => {
  const cached = localStorage.getItem("replace_text");
  if (cached) {
    try {
      // ยังไม่หมดอายุ → ใช้ของเดิม ไม่ต้องยิง API ซ้ำ
      if (JSON.parse(cached).datetime_create > new Date().getTime()) return;
    } catch (e) {}
  }
  const texts = await fetchTexts();
  localStorage.setItem("replace_text", JSON.stringify({ datetime_create: new Date().getTime() + CACHE_MS, ...texts }));
};

// แทนคำในหน้าจอ — เดิน DOM แล้วแก้เฉพาะ text node
// เช็ค text.includes(คำใหม่) ก่อน เพื่อกันแทนซ้ำเมื่อฟังก์ชันถูกเรียกหลายรอบ
export const replaceTextHTML = async () => {
  let cached = {};
  try {
    cached = JSON.parse(localStorage.getItem("replace_text")) || {};
  } catch (e) {}

  const texts = {};
  TEXT_RULES.forEach((rule) => {
    texts[rule.key] = cached[rule.key] || rule.fallback;
  });

  document.body.querySelectorAll("*").forEach((el) => {
    el.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      let text = node.textContent;
      TEXT_RULES.forEach((rule) => {
        text = text.includes(texts[rule.key]) ? text : text.replace(rule.pattern, texts[rule.key]);
      });
      if (node.textContent !== text) node.textContent = text;
    });
  });
};
